import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Plus, X } from 'lucide-react';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';
import TargetPicker, { NEW_PASS, NEW_FAIL, NEW_STEP } from './TargetPicker';
import * as outcomes from './outcomes';

/**
 * What this step returns, and where each answer goes — one list.
 *
 * The type is the only thing asked about the output; the routes fall out of
 * it. True/false has exactly two rows. "One of" has a row per option. A
 * number has a row per range the person writes, with each end's number and
 * whether it is included. Ranges may not overlap (the row says which, and
 * nothing is saved until it is fixed); a stretch no range covers is named
 * and goes to "Anything else". See ./outcomes.js for the stored form.
 */

const field = 'h-7 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2 text-[12px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--border-strong)] disabled:opacity-60';
const rowCls = 'rounded-md border border-[color:var(--border-default)] px-2.5 py-1.5';

const TYPES = [
  { value: 'boolean', label: 'True / false' },
  { value: 'number', label: 'Number' },
  { value: 'enum', label: 'One of' },
];

function Row({ label, children, danger = false }) {
  return (
    <div className={`flex items-center gap-2 ${rowCls} ${danger ? 'border-[color:var(--feedback-warning)]' : ''}`}>
      <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-[color:var(--text-primary)]">{label}</span>
      <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]">goes to</span>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Number: ranges
// ---------------------------------------------------------------------------

const toDraft = (r) => ({ min: String(r.min), max: String(r.max), min_inclusive: r.min_inclusive !== false, max_inclusive: r.max_inclusive !== false });
const parse = (d) => ({
  min: d.min.trim() === '' ? NaN : Number(d.min), max: d.max.trim() === '' ? NaN : Number(d.max),
  ...(d.min_inclusive ? {} : { min_inclusive: false }), ...(d.max_inclusive ? {} : { max_inclusive: false }),
});
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** One end of a range: which side of the number, and the number. */
function Bound({ end, draft, onChange, disabled, index }) {
  const incl = draft[`${end}_inclusive`];
  const sym = end === 'min' ? (incl ? '≥' : '>') : (incl ? '≤' : '<');
  const words = end === 'min' ? (incl ? 'from, including' : 'above, not including') : (incl ? 'up to, including' : 'below, not including');
  return (
    <span className="inline-flex h-7 items-stretch overflow-hidden rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] focus-within:border-[color:var(--border-strong)]">
      <button type="button" disabled={disabled} onClick={() => onChange({ ...draft, [`${end}_inclusive`]: !incl })}
        aria-label={`Range ${index + 1} ${end === 'min' ? 'start' : 'end'}: ${words}. Click to switch.`}
        title={`${words} ${draft[end]}: click to ${incl ? 'leave it out' : 'include it'}`}
        className="w-7 border-r border-[color:var(--border-default)] text-[12.5px] font-semibold text-[color:var(--trodo-primary)] hover:bg-surface-hover disabled:opacity-60">
        {sym}
      </button>
      <input type="number" step="any" inputMode="decimal" disabled={disabled} value={draft[end]}
        aria-label={`Range ${index + 1} ${end === 'min' ? 'from' : 'to'}`}
        onChange={(e) => onChange({ ...draft, [end]: e.target.value })}
        className="w-[68px] bg-transparent px-1.5 font-mono text-[12px] tabular-nums text-[color:var(--text-primary)] outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
    </span>
  );
}

function NumberRanges({ graph, nodeId, node, shape, fixed, readOnly, onChange, pick }) {
  const spec = shape.spec;
  const lo = Number(spec.min) || 0; const hi = Number(spec.max ?? 1);
  const [drafts, setDrafts] = useState(() => shape.ranges.map(toDraft));

  // Follow the graph (another node, an undo, a change from the canvas) unless
  // the draft already says the same thing — so "0." is not rewritten to "0"
  // under the cursor.
  useEffect(() => {
    setDrafts((cur) => (same(cur.map(parse), shape.ranges) ? cur : shape.ranges.map(toDraft)));
  }, [nodeId, shape.ranges]);

  const parsed = drafts.map(parse);
  const check = useMemo(() => outcomes.analyse(parsed, lo, hi), [JSON.stringify(parsed), lo, hi]);   // eslint-disable-line react-hooks/exhaustive-deps

  const targets = shape.outcomes.filter((o) => o.key !== outcomes.ELSE).map((o) => o.to);
  const elseOutcome = shape.outcomes.find((o) => o.key === outcomes.ELSE);

  /** Save when the whole list is sound; otherwise keep the draft and show why. */
  const commit = (nextDrafts, nextTargets) => {
    setDrafts(nextDrafts);
    const next = outcomes.setRanges(node, nextDrafts.map(parse), nextTargets);
    if (next) onChange(next);
  };

  const edit = (i) => (d) => commit(drafts.map((x, j) => (j === i ? d : x)));
  const remove = (i) => commit(drafts.filter((_, j) => j !== i), targets.filter((_, j) => j !== i));
  const add = () => {
    if (!check.ok) return;
    const nextRanges = outcomes.withNewRange(parsed, lo, hi);
    commit(nextRanges.map(toDraft), nextRanges.length > parsed.length ? [...targets, null] : [...targets.slice(0, -1), targets[targets.length - 1] ?? null, null]);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-[12px] text-[color:var(--text-secondary)]">
        <span>Scores run from</span>
        <input type="number" step="any" value={spec.min ?? ''} disabled={fixed} aria-label="Range from"
          onChange={(e) => onChange(outcomes.setRange(node, Number(e.target.value), spec.max))} className={`${field} w-20 font-mono`} />
        <span>to</span>
        <input type="number" step="any" value={spec.max ?? ''} disabled={fixed} aria-label="Range to"
          onChange={(e) => onChange(outcomes.setRange(node, spec.min, Number(e.target.value)))} className={`${field} w-20 font-mono`} />
      </div>

      <ol className="space-y-1.5" data-testid="outcomes" aria-label="Ranges">
        {drafts.map((d, i) => {
          const problem = check.problems[i];
          return (
            <li key={i} className={`${rowCls} ${problem ? 'border-[color:var(--feedback-error)]' : !targets[i] ? 'border-[color:var(--feedback-warning)]' : ''}`}>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                <span className="flex items-center gap-1.5">
                  <Bound end="min" draft={d} index={i} disabled={fixed} onChange={edit(i)} />
                  <span className="text-[11px] text-[color:var(--text-muted)]">and</span>
                  <Bound end="max" draft={d} index={i} disabled={fixed} onChange={edit(i)} />
                </span>
                <span className="flex min-w-0 flex-1 items-center justify-end gap-2">
                  <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]">goes to</span>
                  <TargetPicker graph={graph} nodeId={nodeId} value={targets[i] ?? null} disabled={readOnly || !!problem} allowClear
                    className="min-w-[140px] flex-1" ariaLabel={`Outcome ${i + 1} target`} onChange={pick(i)} />
                  {!fixed && drafts.length > 1 && (
                    <button type="button" aria-label={`Remove range ${i + 1}`} onClick={() => remove(i)}
                      className="shrink-0 rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-danger"><X size={12} /></button>
                  )}
                </span>
              </div>
              {problem && <p role="alert" className="mt-1 text-[11px] text-danger">{problem} Not saved until it is fixed.</p>}
            </li>
          );
        })}
      </ol>

      {elseOutcome && check.ok && (
        <div className="space-y-1.5">
          <div role="status" className="flex items-start gap-2 rounded-md bg-warning-soft px-2.5 py-1.5 text-[11.5px] text-warning">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>
              No range covers {shape.gaps.map((g) => outcomes.gapLabel(g)).join(', and ')}.
              {' '}Values there go to “Anything else”, or add a range for them.
            </span>
          </div>
          <Row label="Anything else" danger={!elseOutcome.to}>
            <TargetPicker graph={graph} nodeId={nodeId} value={elseOutcome.to} disabled={readOnly} allowClear
              ariaLabel={`Outcome ${shape.outcomes.length} target`} onChange={pick(shape.outcomes.length - 1)} />
          </Row>
        </div>
      )}

      {!fixed && (
        <button type="button" onClick={add} disabled={!check.ok}
          title={check.ok ? (check.gaps.length ? 'Covers the first stretch no range covers' : 'Splits the top range in two') : 'Fix the range marked in red first'}
          className="inline-flex items-center gap-1 rounded-md border border-dashed border-[color:var(--border-default)] px-2 py-1 text-[11.5px] text-[color:var(--text-secondary)] hover:bg-surface-hover disabled:opacity-50">
          <Plus size={11} /> Add a range
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One of: options as chips
// ---------------------------------------------------------------------------

function OptionChip({ value, index, disabled, onRename, onRemove, canRemove }) {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);
  const done = () => { const t = text.trim(); if (t && t !== value) onRename(t); else setText(value); };
  return (
    <span className="inline-flex h-7 items-center gap-0.5 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] pl-2 pr-0.5 focus-within:border-[color:var(--border-strong)]">
      <input value={text} disabled={disabled} aria-label={`Option ${index + 1}`} size={Math.max(2, text.length)}
        onChange={(e) => setText(e.target.value)} onBlur={done}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setText(value); e.currentTarget.blur(); } }}
        className="bg-transparent font-mono text-[12px] text-[color:var(--text-primary)] outline-none" />
      {canRemove && !disabled && (
        <button type="button" aria-label={`Remove option ${value}`} onClick={onRemove}
          className="rounded p-0.5 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-danger"><X size={11} /></button>
      )}
    </span>
  );
}

function Options({ node, values, fixed, onChange }) {
  const [adding, setAdding] = useState('');
  const set = (next) => { const r = outcomes.setMembers(node, next); if (r) onChange(r); };
  const add = () => {
    const parts = adding.split(',').map((s) => s.trim()).filter(Boolean).filter((s) => !values.includes(s));
    if (parts.length) set([...values, ...parts]);
    setAdding('');
  };
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">Options</div>
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Options">
        {values.map((v, i) => (
          <OptionChip key={`${i}-${v}`} value={v} index={i} disabled={fixed} canRemove={values.length > 1}
            onRename={(t) => { if (!values.includes(t)) set(values.map((x, j) => (j === i ? t : x))); }}
            onRemove={() => set(values.filter((_, j) => j !== i))} />
        ))}
        {!fixed && (
          <input value={adding} placeholder="Add an option" aria-label="Add an option"
            onChange={(e) => setAdding(e.target.value)} onBlur={add}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
            className="h-7 w-32 rounded-md border border-dashed border-[color:var(--border-default)] bg-transparent px-2 text-[12px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)] focus:border-[color:var(--border-strong)] focus:border-solid" />
        )}
      </div>
    </div>
  );
}

export default function OutcomesEditor({ graph, nodeId, shape, onChange, onNewVerdict, onNewStep, readOnly = false, lockShape = false }) {
  const node = graph.nodes[nodeId];
  const spec = shape.spec;
  // Some instruments decide what they return (a Filter is always true/false, a
  // Similarity step always a number): the routes stay editable, the shape does not.
  const fixed = readOnly || lockShape;

  const pick = (index) => (choice) => {
    if (choice === NEW_STEP) { onNewStep(index); return; }
    if (choice === NEW_PASS || choice === NEW_FAIL) { onNewVerdict(index, choice === NEW_PASS); return; }
    onChange({ edges: outcomes.route(node, index, choice || null) });
  };

  return (
    <div className="space-y-3">
      {!lockShape && (
        <SegmentedTabs ariaLabel="Returns" value={shape.type} options={TYPES}
          onChange={(t) => { if (!readOnly) onChange(outcomes.setType(node, t)); }} />
      )}

      {shape.type === 'number' ? (
        <NumberRanges graph={graph} nodeId={nodeId} node={node} shape={shape} fixed={fixed} readOnly={readOnly} onChange={onChange} pick={pick} />
      ) : (
        <>
          {shape.type === 'enum' && <Options node={node} values={spec.values || []} fixed={fixed} onChange={onChange} />}
          <div className="space-y-1.5" data-testid="outcomes">
            {shape.outcomes.map((o, i) => (
              <Row key={o.key} label={o.label} danger={!o.to}>
                <TargetPicker graph={graph} nodeId={nodeId} value={o.to} disabled={readOnly} allowClear
                  ariaLabel={`Outcome ${i + 1} target`} onChange={pick(i)} />
              </Row>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
